/**
 * Suggested actions the demo engine can justify from the same figures the
 * Overview and Alerts show. Every recommendation's action is a drill-down to
 * where a person reviews and carries it out — nothing here calls an
 * automation, campaign or listing API on its own (pivot doc §3a: approval
 * required, nothing executes silently).
 */

import type { Recommendation, RecommendationConfidence } from '@/api/types'
import { formatCurrency, formatPercent, formatRatio } from '@/design/format'
import { addDays, DEFAULT_FILTERS, type GlobalFilters } from '@/lib/filters'

import type { CompetitionStatus, PeriodTotals } from '../generators/engine'
import { scopeFor, type DemoDataset } from '../generators/engine'
import type { CompetitionFixture, TenantFixture } from '../tenants/types'

type Draft = Omit<Recommendation, 'createdAt' | 'state'>
type Money = { currency: string; locale: string }

const SCALE_ROAS_FLOOR = 2

function confidenceFor(value: number, [medium, high]: readonly [number, number]): RecommendationConfidence {
  if (value >= high) return 'high'
  if (value >= medium) return 'medium'
  return 'low'
}

/** Scale budget on whichever paid channel returned the most over the last week, if it clears the floor. */
function buildScaleBudgetRecommendation(tenant: TenantFixture, recent: PeriodTotals, money: Money): Draft | null {
  const paidChannels = tenant.channels.flatMap((channel) => {
    const totals = recent.channels.get(channel.id)
    return totals && totals.spend > 0 ? [{ channel, totals }] : []
  })
  const best = [...paidChannels].sort(
    (a, b) => b.totals.attributedRevenue / b.totals.spend - a.totals.attributedRevenue / a.totals.spend,
  )[0]
  if (!best) return null

  const roas = best.totals.attributedRevenue / best.totals.spend
  if (roas < SCALE_ROAS_FLOOR) return null

  const entity = { type: 'channel' as const, id: best.channel.id, label: best.channel.name }
  const uplift = best.totals.spend * 0.2 * roas
  return {
    id: `rec_scale_${best.channel.id}`,
    title: `Increase budget on ${best.channel.name}`,
    category: 'advertising',
    entity,
    explanation: `${best.channel.name} returned ${formatRatio(roas)} on ad spend over the last 7 days, the strongest of your paid channels. A modest budget increase is likely to add revenue at a similar rate before returns taper.`,
    metrics: [
      { label: 'ROAS', value: formatRatio(roas) },
      { label: '7-day spend', value: formatCurrency(best.totals.spend, money) },
      { label: 'Attributed revenue', value: formatCurrency(best.totals.attributedRevenue, money) },
    ],
    estimatedImpact: `+${formatCurrency(uplift, money)} per week from a 20% budget increase`,
    confidence: confidenceFor(roas, [3, 5]),
    action: { label: 'Create campaign', drillDown: { path: '/marketing/campaigns/new', entity } },
  }
}

/** Always relevant, so the page is rarely empty even on a quiet demo tenant. */
function buildWinBackRecommendation(recent: PeriodTotals): Draft {
  const totalCustomers = recent.newCustomers + recent.returningCustomers
  const returningShare = totalCustomers > 0 ? recent.returningCustomers / totalCustomers : 0
  return {
    id: 'rec_audience_returning',
    title: 'Build a win-back audience of returning customers',
    category: 'customer',
    entity: null,
    explanation: `${formatPercent(returningShare, 0)} of the last 7 days' customers had bought before. A segment targeting them supports a win-back campaign without spending on new-customer acquisition.`,
    metrics: [
      { label: 'Returning customers', value: `${recent.returningCustomers}` },
      { label: 'Share of customers', value: formatPercent(returningShare, 0) },
    ],
    estimatedImpact: `Reaches ${recent.returningCustomers} customers at no acquisition cost`,
    confidence: confidenceFor(returningShare, [0.3, 0.5]),
    action: { label: 'Create audience', drillDown: { path: '/marketing/audiences' } },
  }
}

interface Paced {
  competition: CompetitionFixture
  status: CompetitionStatus
}

/** Capitalise on the competition furthest ahead of pace, with enough time left for a push to matter. */
function buildPromoteAheadRecommendation(paced: readonly Paced[]): Draft | null {
  const ahead = [...paced]
    .filter((entry) => entry.status.pacing === 'ahead' && entry.status.daysLeft > 2)
    .sort((a, b) => b.status.sellThrough - a.status.sellThrough)[0]
  if (!ahead) return null

  const entity = { type: 'competition' as const, id: ahead.competition.id, label: ahead.competition.name }
  return {
    id: `rec_promote_${ahead.competition.id}`,
    title: `Promote ${ahead.competition.name} while demand is strong`,
    category: 'competition',
    entity,
    explanation: `${ahead.competition.name} is ${formatPercent(ahead.status.sellThrough, 0)} sold with ${ahead.status.daysLeft} days left, ahead of the pace needed to sell out on schedule. A short push now is likely to convert the remaining entries faster.`,
    metrics: [
      { label: 'Sold to date', value: formatPercent(ahead.status.sellThrough, 0) },
      { label: 'Days left', value: `${ahead.status.daysLeft}` },
      { label: 'Entries per day', value: `${Math.round(ahead.status.velocityPerDay)}` },
    ],
    estimatedImpact: 'Faster sell-out, freeing the slot for the next competition sooner',
    confidence: confidenceFor(ahead.status.sellThrough, [0.5, 0.75]),
    action: { label: 'Open competition', drillDown: { path: `/analytics/competitions/${ahead.competition.id}`, entity } },
  }
}

/** Automate a closing reminder for whichever competition is soonest to close while still behind pace. */
function buildPacingReminderRecommendation(paced: readonly Paced[]): Draft | null {
  const behind = [...paced]
    .filter((entry) => entry.status.pacing === 'behind' && entry.status.daysLeft > 0 && entry.status.daysLeft <= 7)
    .sort((a, b) => a.status.daysLeft - b.status.daysLeft)[0]
  if (!behind) return null

  const entity = { type: 'competition' as const, id: behind.competition.id, label: behind.competition.name }
  return {
    id: `rec_automate_${behind.competition.id}`,
    title: `Automate a closing reminder for ${behind.competition.name}`,
    category: 'competition',
    entity,
    explanation: `${behind.competition.name} closes in ${behind.status.daysLeft} ${behind.status.daysLeft === 1 ? 'day' : 'days'} and is behind the pace needed to sell out, with ${formatPercent(1 - behind.status.sellThrough, 0)} still unsold.`,
    metrics: [
      { label: 'Sold to date', value: formatPercent(behind.status.sellThrough, 0) },
      { label: 'Days left', value: `${behind.status.daysLeft}` },
    ],
    estimatedImpact: 'Recovers some of the pacing gap before the close date',
    confidence: behind.status.daysLeft <= 3 ? 'high' : 'medium',
    action: { label: 'Create automation', drillDown: { path: '/automation/workflows', entity } },
  }
}

export function buildRecommendations(dataset: DemoDataset, filters: GlobalFilters): Recommendation[] {
  const { tenant, today, now } = dataset
  const money: Money = { currency: tenant.currency, locale: tenant.locale }
  const scope = scopeFor(tenant, { ...DEFAULT_FILTERS, environments: filters.environments })
  if (!scope.includesProduction) return []

  const recent = dataset.period({ from: addDays(today, -6), to: today, days: 7 }, scope)
  const paced: Paced[] = dataset.onSaleOn(today).map((competition) => ({ competition, status: dataset.competitionStatus(competition) }))

  const drafts = [
    buildScaleBudgetRecommendation(tenant, recent, money),
    buildWinBackRecommendation(recent),
    buildPromoteAheadRecommendation(paced),
    buildPacingReminderRecommendation(paced),
  ].filter((draft): draft is Draft => draft !== null)

  return drafts
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((draft, index) => ({
      ...draft,
      state: 'new' as const,
      createdAt: new Date(now.getTime() - (index * 53 + 30) * 60_000).toISOString(),
    }))
}
